import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { PageHeader, StatBadge } from "@/components/admin/AdminLayout";
import { AreaChart, Area, ResponsiveContainer, XAxis, YAxis, Tooltip, CartesianGrid } from "recharts";
import { CalendarDays, Euro, AlertTriangle, UserRoundX, TrendingUp, Clock, Users, Activity } from "lucide-react";
import { useLang } from "@/lib/lang";
import { Link } from "react-router-dom";

const KPI = ({ label, value, sub, icon: Icon, accent, testid }) => (
  <div data-testid={testid} className="border border-slate-200 bg-white p-6 relative overflow-hidden group hover:border-[#f59e0b]/40 transition-colors">
    <div className="flex items-start justify-between mb-8">
      <span className="label-caps">{label}</span>
      <Icon className={`w-4 h-4 ${accent ? "text-[#f59e0b]" : "text-slate-500"}`} strokeWidth={1.5} />
    </div>
    <div className="font-display text-4xl text-[#0a0f1e] leading-none">{value}</div>
    {sub && <div className="text-xs text-slate-500 mt-3">{sub}</div>}
    <div className="absolute bottom-0 left-0 h-px w-0 bg-[#f59e0b] group-hover:w-full transition-all duration-500"></div>
  </div>
);

const PendingBox = ({ to, label, count, testid, t }) => (
  <Link to={to} data-testid={testid} className={`border p-4 hover:border-[#f59e0b] transition-colors ${count > 0 ? "border-amber-300 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
    <div className="text-[10px] uppercase tracking-widest text-slate-600 font-semibold">{label}</div>
    <div className={`font-display text-3xl mt-2 ${count > 0 ? "text-amber-700" : "text-slate-400"}`}>{count}</div>
    {count > 0 && <div className="text-xs text-amber-700 mt-1">{t("action_review")}</div>}
  </Link>
);

const PERIODS = [
  { key: "daily", label: "7d" },
  { key: "weekly", label: "4w" },
  { key: "monthly", label: "6m" },
];

export default function Dashboard() {
  const { t, lang } = useLang();
  const [stats, setStats] = useState(null);
  const [chart, setChart] = useState(null);
  const [period, setPeriod] = useState("daily");
  const [upcoming, setUpcoming] = useState([]);
  const [alerts, setAlerts] = useState([]);
  const [activity, setActivity] = useState([]);
  const [approvals, setApprovals] = useState(null);

  useEffect(() => {
    api.get("/admin/dashboard/stats").then((r) => setStats(r.data));
    api.get("/admin/dashboard/upcoming-trips").then((r) => setUpcoming(r.data));
    api.get("/admin/dashboard/document-alerts").then((r) => setAlerts(r.data));
    api.get("/admin/dashboard/activity").then((r) => setActivity(r.data));
    api.get("/admin/approvals/pending").then((r) => setApprovals(r.data));
  }, []);

  useEffect(() => {
    api.get(`/admin/dashboard/revenue-chart?period=${period}`).then((r) => setChart(r.data));
  }, [period]);

  return (
    <div className="p-10 max-w-[1600px]">
      <PageHeader eyebrow={t("dashboard_eyebrow")} title={t("dashboard_title")} description={t("dashboard_description")}>
        <span className="text-xs font-mono text-slate-500">{new Date().toLocaleString(lang.toLowerCase() === "pt" ? "pt-PT" : lang.toLowerCase(), { dateStyle: "medium", timeStyle: "short" })}</span>
      </PageHeader>

      {/* KPI Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-4 gap-5 mb-10">
        <KPI testid="kpi-today-bookings" label={t("kpi_today_bookings")} value={stats?.today_bookings ?? "—"} sub={t("kpi_today_bookings_sub")} icon={CalendarDays} accent />
        <KPI testid="kpi-today-revenue" label={t("kpi_today_revenue")} value={stats ? `€${stats.today_revenue.toFixed(2)}` : "—"} sub={t("kpi_today_revenue_sub")} icon={Euro} accent />
        <KPI testid="kpi-pending" label={t("kpi_pending_approvals")} value={stats?.pending_approvals ?? "—"} sub={t("kpi_pending_approvals_sub")} icon={AlertTriangle} />
        <KPI testid="kpi-unassigned" label={t("kpi_unassigned")} value={stats?.unassigned_bookings ?? "—"} sub={t("kpi_unassigned_sub")} icon={UserRoundX} />
      </div>

      {/* Approvals breakdown */}
      {approvals && (
        <div className="mb-10 bg-white border border-slate-200 p-6 shadow-sm">
          <div className="flex items-center justify-between mb-4">
            <p className="text-[10px] uppercase tracking-widest text-slate-500 font-semibold">{t("pending_approvals_title")}</p>
            <span className="text-[10px] uppercase tracking-widest text-[#f59e0b] font-semibold">{t("live_queue")}</span>
          </div>
          <div className="grid grid-cols-4 gap-4">
            <PendingBox to="/admin/drivers?status=pending" label={t("nav_drivers")} count={approvals.drivers} testid="pending-drivers" t={t} />
            <PendingBox to="/admin/vehicles" label={t("nav_vehicles")} count={approvals.vehicles} testid="pending-vehicles" t={t} />
            <PendingBox to="/admin/fleets" label={t("nav_fleets")} count={approvals.fleets} testid="pending-fleets" t={t} />
            <PendingBox to="/admin/partners" label={t("nav_partners")} count={approvals.partners} testid="pending-partners" t={t} />
          </div>
        </div>
      )}

      {/* Chart + upcoming */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5 mb-10">
        <div className="lg:col-span-2 border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="label-caps mb-1">{t("revenue_eyebrow")}</p>
              <h2 className="font-display text-2xl text-[#0a0f1e]">{t("revenue_title")}</h2>
            </div>
            <div className="flex border border-slate-200">
              {PERIODS.map((p) => (
                <button
                  key={p.key}
                  onClick={() => setPeriod(p.key)}
                  data-testid={`period-${p.key}`}
                  className={`px-4 py-1.5 text-xs uppercase tracking-wide transition-colors ${
                    period === p.key ? "bg-[#f59e0b] text-[#0a0f1e]" : "text-slate-500 hover:text-[#0a0f1e]"
                  }`}
                >{p.label}</button>
              ))}
            </div>
          </div>
          <div className="h-64">
            <ResponsiveContainer>
              <AreaChart data={chart?.series || []} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <defs>
                  <linearGradient id="revGold" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="5%" stopColor="#f59e0b" stopOpacity={0.4}/>
                    <stop offset="95%" stopColor="#f59e0b" stopOpacity={0}/>
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis dataKey="label" stroke="#64748b" fontSize={11} />
                <YAxis stroke="#64748b" fontSize={11} tickFormatter={(v) => `€${v}`} />
                <Tooltip contentStyle={{ background: "#ffffff", border: "1px solid #f59e0b", borderRadius: 0, color: "#0a0f1e" }} formatter={(v) => [`€${v}`, t("revenue_eyebrow")]} />
                <Area type="monotone" dataKey="value" stroke="#f59e0b" strokeWidth={2} fill="url(#revGold)" />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between mb-6">
            <p className="label-caps">{t("next_24h")}</p>
            <Clock className="w-4 h-4 text-[#f59e0b]" />
          </div>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {upcoming.length === 0 && <div className="text-xs text-slate-500">{t("no_upcoming_trips")}</div>}
            {upcoming.map((tr) => (
              <div key={tr.id} data-testid={`upcoming-${tr.id}`} className="border-l-2 border-[#f59e0b] pl-3 py-1">
                <div className="text-[#0a0f1e] text-sm font-medium">{new Date(tr.service_datetime).toLocaleTimeString(lang.toLowerCase() === "pt" ? "pt-PT" : lang.toLowerCase(), { hour: "2-digit", minute: "2-digit" })}</div>
                <div className="text-xs text-slate-500">{tr.client_name} · {tr.type}</div>
                <div className="text-xs text-slate-500 truncate">{tr.pickup_address} → {tr.destination_address}</div>
                <div className="mt-1"><StatBadge status={tr.status} /></div>
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Alerts + Activity */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="label-caps mb-1">{t("docs_expiry_eyebrow")}</p>
              <h3 className="font-display text-xl text-[#0a0f1e]">{t("compliance_watchlist")}</h3>
            </div>
            <AlertTriangle className="w-4 h-4 text-amber-400" />
          </div>
          <div className="space-y-3">
            {alerts.length === 0 && <div className="text-xs text-slate-500">{t("docs_all_valid")}</div>}
            {alerts.slice(0, 8).map((a, i) => (
              <div key={i} className="flex items-center justify-between text-sm border-b border-slate-200 pb-2">
                <div>
                  <div className="text-[#0a0f1e]">{a.entity_name}</div>
                  <div className="text-xs text-slate-500 uppercase">{a.document_type}</div>
                </div>
                <div className="text-right">
                  <div className="text-xs font-mono text-slate-500">{a.expiry_date}</div>
                  <StatBadge status={a.status} />
                </div>
              </div>
            ))}
          </div>
        </div>

        <div className="border border-slate-200 bg-white p-6">
          <div className="flex items-center justify-between mb-6">
            <div>
              <p className="label-caps mb-1">{t("activity_eyebrow")}</p>
              <h3 className="font-display text-xl text-[#0a0f1e]">{t("activity_recent")}</h3>
            </div>
            <Activity className="w-4 h-4 text-[#f59e0b]" />
          </div>
          <div className="space-y-3 max-h-72 overflow-y-auto">
            {activity.map((a, i) => (
              <div key={i} className="text-sm text-slate-700 border-l border-slate-200 pl-3">
                <div>{a.message}</div>
                <div className="text-xs text-slate-500 font-mono mt-0.5">{new Date(a.at).toLocaleString(lang.toLowerCase() === "pt" ? "pt-PT" : lang.toLowerCase(), { dateStyle: "short", timeStyle: "short" })}</div>
              </div>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
